import type { components } from '../../api-types';
import {
  MULTIPAC_SPLIT,
  SCOPE_OVERLAP,
  type ConflictingManifest,
  type ConflictingPair,
  type ScopeConflict
} from '$lib/utils/scopeConflict';

export interface ScopePreview {
  diff: components['schemas']['ScopeDiff'];
  conflict?: ScopeConflict | null;
}

export type ScopeEntityType = components['schemas']['ScopeEntityType'];
export type ScopePreviewChange = components['schemas']['ScopePreviewChange'];
export type ScopePreviewDetail = components['schemas']['ScopePreviewDetail'];
export interface ScopePreviewDetails {
  entityType: ScopeEntityType;
  change: ScopePreviewChange;
  preview: ScopePreview;
  content: ScopePreviewDetail[];
}

export const SCOPE_ENTITY_TYPES: Record<keyof ScopePreview['diff'], ScopeEntityType> = {
  countries: 'COUNTRY',
  chains: 'CHAIN_ID',
  subChains: 'SUB_CHAIN_ID',
  regions: 'REGION_ID',
  locations: 'LOCATION_ID',
  installations: 'INSTALLATION_ID'
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isOptionalString(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === 'string';
}

function mapLevelDiff(value: unknown, level: string): components['schemas']['ScopeLevelDiff'] {
  if (!isRecord(value)) {
    throw new Error(`Invalid scope preview diff: ${level} must be a count record`);
  }
  const { current, proposed, added, removed } = value;
  if (!isCount(current) || !isCount(proposed) || !isCount(added) || !isCount(removed)) {
    throw new Error(
      `Invalid scope preview diff: ${level} requires nonnegative finite current, proposed, added and removed counts`
    );
  }
  return { current, proposed, added, removed };
}

function mapManifests(value: unknown): ConflictingManifest[] | null {
  if (!isArray(value)) {
    return null;
  }
  const manifests: ConflictingManifest[] = [];
  for (const item of value) {
    if (!isRecord(item)) {
      return null;
    }
    const { manifestId, manifestName, installationCount, sampleSerials } = item;
    if (
      typeof manifestId !== 'string' ||
      !isOptionalString(manifestName) ||
      !isCount(installationCount) ||
      !isArray(sampleSerials)
    ) {
      return null;
    }
    const serials: string[] = [];
    for (const serial of sampleSerials) {
      if (typeof serial !== 'string') {
        return null;
      }
      serials.push(serial);
    }
    manifests.push({
      manifestId,
      ...(manifestName !== undefined ? { manifestName } : {}),
      installationCount,
      sampleSerials: serials
    });
  }
  return manifests;
}

function mapPairs(value: unknown): ConflictingPair[] | null {
  if (!isArray(value)) {
    return null;
  }
  const pairs: ConflictingPair[] = [];
  for (const item of value) {
    if (!isRecord(item)) {
      return null;
    }
    const { includedSerial, excludedSerial, excludedManifestId, excludedManifestName } = item;
    if (
      typeof includedSerial !== 'string' ||
      typeof excludedSerial !== 'string' ||
      !isOptionalString(excludedManifestId) ||
      !isOptionalString(excludedManifestName)
    ) {
      return null;
    }
    pairs.push({
      includedSerial,
      excludedSerial,
      ...(excludedManifestId !== undefined ? { excludedManifestId } : {}),
      ...(excludedManifestName !== undefined ? { excludedManifestName } : {})
    });
  }
  return pairs;
}

function mapConflict(value: unknown): ScopeConflict {
  if (!isRecord(value) || typeof value.type !== 'string') {
    return { type: 'UNKNOWN' };
  }
  if (value.type === SCOPE_OVERLAP && isCount(value.totalInstallations)) {
    const manifests = mapManifests(value.manifests);
    if (manifests !== null) {
      return { type: SCOPE_OVERLAP, totalInstallations: value.totalInstallations, manifests };
    }
  }
  if (value.type === MULTIPAC_SPLIT && isCount(value.totalPairs)) {
    const samplePairs = mapPairs(value.samplePairs);
    if (samplePairs !== null) {
      return { type: MULTIPAC_SPLIT, totalPairs: value.totalPairs, samplePairs };
    }
  }
  if (value.type === SCOPE_OVERLAP || value.type === MULTIPAC_SPLIT) {
    return { type: 'UNKNOWN' };
  }
  return { type: value.type };
}

export function mapScopePreview(raw: unknown): ScopePreview {
  const body = isRecord(raw) && 'data' in raw ? raw.data : raw;
  if (!isRecord(body) || !isRecord(body.diff)) {
    throw new Error('Invalid scope preview diff: expected an object containing diff');
  }
  const diff: components['schemas']['ScopeDiff'] = {
    countries: mapLevelDiff(body.diff.countries, 'countries'),
    chains: mapLevelDiff(body.diff.chains, 'chains'),
    subChains: mapLevelDiff(body.diff.subChains, 'subChains'),
    regions: mapLevelDiff(body.diff.regions, 'regions'),
    locations: mapLevelDiff(body.diff.locations, 'locations'),
    installations: mapLevelDiff(body.diff.installations, 'installations')
  };
  if (body.conflict === undefined) {
    return { diff };
  }
  return {
    diff,
    conflict: body.conflict === null ? null : mapConflict(body.conflict)
  };
}

export function mapScopePreviewDetails(raw: unknown): ScopePreviewDetails {
  const body = isRecord(raw) && 'data' in raw ? raw.data : raw;
  if (
    !isRecord(body) ||
    !Object.values(SCOPE_ENTITY_TYPES).includes(body.entityType as ScopeEntityType) ||
    (body.change !== 'ADDED' && body.change !== 'REMOVED') ||
    !isArray(body.content)
  ) {
    throw new Error('Invalid scope preview details');
  }
  const content = body.content.map((row): ScopePreviewDetail => {
    if (
      !isRecord(row) ||
      typeof row.id !== 'string' ||
      ![
        'name',
        'serialNumber',
        'locationId',
        'locationName',
        'chainId',
        'chainName',
        'subChainId',
        'subChainName',
        'region'
      ].every((field) => isOptionalString(row[field]))
    ) {
      throw new Error('Invalid scope preview detail row');
    }
    return row as ScopePreviewDetail;
  });
  return {
    entityType: body.entityType as ScopeEntityType,
    change: body.change,
    preview: mapScopePreview(body.preview),
    content
  };
}
